import { cloneDeep, isArray, isPlainObject } from 'lodash';

import { BaseExportDataModel } from '../../../../shared/models';

/**
 * Configuration fields needed to generate a deduplication hash for export data
 */
export type ExportDataDeduplicationInput = Pick<
  BaseExportDataModel,
  'workspaceId' | 'entity' | 'type' | 'config' | 'parameters'
>;

/**
 * Options for generating export data deduplication hash
 */
export interface GenerateExportDataDeduplicationHashOptions {
  input: ExportDataDeduplicationInput;
  hashFunction: (normalizedString: string) => string;
}

/**
 * Generate a stable deduplication hash for export data configuration.
 * This helper follows the same pattern used in the risk service for stable object hashing.
 *
 * @param options Configuration object containing input data and hash function
 * @returns Hashed string suitable for deduplication
 */
export function generateExportDataDeduplicationHash({
  input,
  hashFunction,
}: GenerateExportDataDeduplicationHashOptions): string {
  const normalizedData = {
    workspaceId: input.workspaceId,
    entity: input.entity,
    type: input.type,
    config: input.config,
    parameters: input.parameters,
  };

  // Deep clone to avoid mutating input
  const normalized = cloneDeep(normalizedData);

  // Now deeply sort all object keys
  const hashInput = JSON.stringify(stableObjectHash(normalized));

  // Apply the provided hash function
  return hashFunction(hashInput);
}

/**
 * Create a stable hash of an object by sorting keys recursively.
 * This ensures consistent hash generation regardless of key insertion order.
 */
function stableObjectHash(obj: unknown): unknown {
  if (!isPlainObject(obj) && !isArray(obj)) {
    return obj;
  }

  if (isArray(obj)) {
    return [...obj]
      .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)))
      .map(stableObjectHash);
  }

  // For plain objects, sort keys and recurse
  return Object.keys(obj as Record<string, unknown>)
    .sort()
    .reduce((acc: Record<string, unknown>, key) => {
      acc[key] = stableObjectHash((obj as Record<string, unknown>)[key]);
      return acc;
    }, {});
}
